import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../App";
import Watermark from '../components/Watermark';

const BOOKMAKER_LABELS = {
  betway_bw: 'Betway', pstbets_bw: 'PST Bets', sunbet_bw: 'Sunbet',
  bet267_bw: 'Bet267', bbets_bw: 'BBets', betxplosion_bw: 'BetXplosion',
};

const PRESETS = [
  {
    key: 'conservative',
    label: '🟢 Conservative',
    desc: '3 legs · Short odds · ~40% chance',
    legs: 3,
    avgOdds: 1.40,
    color: 'var(--success)',
  },
  {
    key: 'balanced',
    label: '🟡 Balanced',
    desc: '4 legs · Medium odds · ~25% chance',
    legs: 4,
    avgOdds: 1.55,
    color: 'var(--warning)',
  },
  {
    key: 'aggressive',
    label: '🟠 Aggressive',
    desc: '5 legs · Higher odds · ~10% chance',
    legs: 5,
    avgOdds: 1.70,
    color: '#f5a623',
  },
  {
    key: 'longshot',
    label: '🔴 Longshot',
    desc: '8 legs · Big odds · Dream return',
    legs: 8,
    avgOdds: 2.50,
    color: 'var(--danger)',
  },
];

function getDifficultyIndicator(prob) {
  if (prob >= 30) return { icon: '🟢', label: 'Likely', color: 'var(--success)' };
  if (prob >= 10) return { icon: '🟡', label: 'Possible', color: 'var(--warning)' };
  if (prob >= 2) return { icon: '🟠', label: 'Unlikely but achievable', color: '#f5a623' };
  return { icon: '🔴', label: 'Dream bet — very unlikely', color: 'var(--danger)' };
}

function calcWinProb(legs, avgOdds) {
  const prob = Math.pow(1 / avgOdds, legs) * 100;
  if (prob < 0.1) return Number(prob.toFixed(3));
  if (prob < 1) return Number(prob.toFixed(2));
  return Number(prob.toFixed(1));
}

function calcReturn(stake, legs, avgOdds) {
  return Number((stake * Math.pow(avgOdds, legs)).toFixed(2));
}

function calcLegsNeeded(stake, targetReturn, avgOdds) {
  if (targetReturn <= stake || avgOdds <= 1) return 2;
  const legs = Math.round(Math.log(targetReturn / stake) / Math.log(avgOdds));
  return Math.min(Math.max(legs, 2), 10);
}

function formatReturn(amount) {
  if (amount >= 1000000) return `P${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `P${(amount / 1000).toFixed(1)}K`;
  return `P${amount.toFixed(2)}`;
}

const SYSTEM_BETS = [
  {
    key: 'trixie',
    name: 'Trixie',
    selections: 3,
    bets: 4,
    minWins: 2,
    description: 'A Trixie consists of 4 bets on 3 selections — 3 doubles and 1 treble. You need at least 2 selections to win to get a return.',
    breakdown: '3 doubles + 1 treble',
    bestFor: 'Low risk, 3 strong selections',
      combinations: (odds, names = []) => {
      const [a, b, c] = odds;
      const [n1, n2, n3] = names.length ? names : ['Sel 1', 'Sel 2', 'Sel 3'];
      return [
        { name: `${n1} + ${n2}`, legs: [a, b], payout: a * b },
        { name: `${n1} + ${n3}`, legs: [a, c], payout: a * c },
        { name: `${n2} + ${n3}`, legs: [b, c], payout: b * c },
        { name: `${n1} + ${n2} + ${n3}`, legs: [a, b, c], payout: a * b * c },
      ];
    },
  },
  {
    key: 'patent',
    name: 'Patent',
    selections: 3,
    bets: 7,
    minWins: 1,
    description: 'A Patent consists of 7 bets on 3 selections — 3 singles, 3 doubles and 1 treble. Even 1 winner returns something.',
    breakdown: '3 singles + 3 doubles + 1 treble',
    bestFor: 'Maximum coverage on 3 selections',
      combinations: (odds, names = []) => {
      const [a, b, c] = odds;
      const [n1, n2, n3] = names.length ? names : ['Sel 1', 'Sel 2', 'Sel 3'];
      return [
        { name: n1, legs: [a], payout: a },
        { name: n2, legs: [b], payout: b },
        { name: n3, legs: [c], payout: c },
        { name: `${n1} + ${n2}`, legs: [a, b], payout: a * b },
        { name: `${n1} + ${n3}`, legs: [a, c], payout: a * c },
        { name: `${n2} + ${n3}`, legs: [b, c], payout: b * c },
        { name: `${n1} + ${n2} + ${n3}`, legs: [a, b, c], payout: a * b * c },
      ];
    },
  },
  {
    key: 'yankee',
    name: 'Yankee',
    selections: 4,
    bets: 11,
    minWins: 2,
    description: 'A Yankee consists of 11 bets on 4 selections — 6 doubles, 4 trebles and 1 four-fold. No singles. Need at least 2 winners.',
    breakdown: '6 doubles + 4 trebles + 1 four-fold',
    bestFor: 'Moderate risk, good returns with 3-4 winners',
      combinations: (odds, names = []) => {
      const [a, b, c, d] = odds;
      const [n1, n2, n3, n4] = names.length ? names : ['Sel 1', 'Sel 2', 'Sel 3', 'Sel 4'];
      return [
        { name: `${n1} + ${n2}`, legs: [a, b], payout: a * b },
        { name: `${n1} + ${n3}`, legs: [a, c], payout: a * c },
        { name: `${n1} + ${n4}`, legs: [a, d], payout: a * d },
        { name: `${n2} + ${n3}`, legs: [b, c], payout: b * c },
        { name: `${n2} + ${n4}`, legs: [b, d], payout: b * d },
        { name: `${n3} + ${n4}`, legs: [c, d], payout: c * d },
        { name: `${n1} + ${n2} + ${n3}`, legs: [a, b, c], payout: a * b * c },
        { name: `${n1} + ${n2} + ${n4}`, legs: [a, b, d], payout: a * b * d },
        { name: `${n1} + ${n3} + ${n4}`, legs: [a, c, d], payout: a * c * d },
        { name: `${n2} + ${n3} + ${n4}`, legs: [b, c, d], payout: b * c * d },
        { name: `${n1} + ${n2} + ${n3} + ${n4}`, legs: [a, b, c, d], payout: a * b * c * d },
      ];
    },
  },
  {
    key: 'lucky15',
    name: 'Lucky 15',
    selections: 4,
    bets: 15,
    minWins: 1,
    description: 'A Lucky 15 consists of 15 bets on 4 selections — 4 singles, 6 doubles, 4 trebles and 1 four-fold. Even 1 winner returns something.',
    breakdown: '4 singles + 6 doubles + 4 trebles + 1 four-fold',
    bestFor: 'Maximum coverage on 4 selections',
      combinations: (odds, names = []) => {
      const [a, b, c, d] = odds;
      const [n1, n2, n3, n4] = names.length ? names : ['Sel 1', 'Sel 2', 'Sel 3', 'Sel 4'];
      return [
        { name: n1, legs: [a], payout: a },
        { name: n2, legs: [b], payout: b },
        { name: n3, legs: [c], payout: c },
        { name: n4, legs: [d], payout: d },
        { name: `${n1} + ${n2}`, legs: [a, b], payout: a * b },
        { name: `${n1} + ${n3}`, legs: [a, c], payout: a * c },
        { name: `${n1} + ${n4}`, legs: [a, d], payout: a * d },
        { name: `${n2} + ${n3}`, legs: [b, c], payout: b * c },
        { name: `${n2} + ${n4}`, legs: [b, d], payout: b * d },
        { name: `${n3} + ${n4}`, legs: [c, d], payout: c * d },
        { name: `${n1} + ${n2} + ${n3}`, legs: [a, b, c], payout: a * b * c },
        { name: `${n1} + ${n2} + ${n4}`, legs: [a, b, d], payout: a * b * d },
        { name: `${n1} + ${n3} + ${n4}`, legs: [a, c, d], payout: a * c * d },
        { name: `${n2} + ${n3} + ${n4}`, legs: [b, c, d], payout: b * c * d },
        { name: `${n1} + ${n2} + ${n3} + ${n4}`, legs: [a, b, c, d], payout: a * b * c * d },
      ];
    },
  },
  {
    key: 'canadian',
    name: 'Canadian (Super Yankee)',
    selections: 5,
    bets: 26,
    minWins: 2,
    description: 'A Canadian consists of 26 bets on 5 selections — 10 doubles, 10 trebles, 5 four-folds and 1 five-fold. No singles.',
    breakdown: '10 doubles + 10 trebles + 5 four-folds + 1 five-fold',
    bestFor: 'Higher returns with 5 selections',
    combinations: null, // Too many to list individually
  },
  {
    key: 'lucky31',
    name: 'Lucky 31',
    selections: 5,
    bets: 31,
    minWins: 1,
    description: 'A Lucky 31 consists of 31 bets on 5 selections — 5 singles, 10 doubles, 10 trebles, 5 four-folds and 1 five-fold.',
    breakdown: '5 singles + 10 doubles + 10 trebles + 5 four-folds + 1 five-fold',
    bestFor: 'Maximum coverage on 5 selections',
    combinations: null,
  },
  {
    key: 'heinz',
    name: 'Heinz',
    selections: 6,
    bets: 57,
    minWins: 2,
    description: 'Named after the 57 varieties. A Heinz consists of 57 bets on 6 selections — 15 doubles, 20 trebles, 15 four-folds, 6 five-folds and 1 six-fold.',
    breakdown: '15 doubles + 20 trebles + 15 four-folds + 6 five-folds + 1 six-fold',
    bestFor: 'Large returns when 4+ selections win',
    combinations: null,
  },
  {
    key: 'lucky63',
    name: 'Lucky 63',
    selections: 6,
    bets: 63,
    minWins: 1,
    description: 'A Lucky 63 consists of 63 bets on 6 selections — 6 singles plus all combinations. Even 1 winner returns something.',
    breakdown: '6 singles + 15 doubles + 20 trebles + 15 four-folds + 6 five-folds + 1 six-fold',
    bestFor: 'Maximum coverage on 6 selections',
    combinations: null,
  },
  {
    key: 'superheinz',
    name: 'Super Heinz',
    selections: 7,
    bets: 120,
    minWins: 2,
    description: 'A Super Heinz consists of 120 bets on 7 selections — 21 doubles, 35 trebles, 35 four-folds, 21 five-folds, 7 six-folds and 1 seven-fold.',
    breakdown: '21 doubles + 35 trebles + 35 four-folds + 21 five-folds + 7 six-folds + 1 seven-fold',
    bestFor: 'Massive returns when 5+ selections win',
    combinations: null,
  },
  {
    key: 'goliath',
    name: 'Goliath',
    selections: 8,
    bets: 247,
    minWins: 2,
    description: 'The ultimate system bet. A Goliath consists of 247 bets on 8 selections covering every possible combination from doubles to an eight-fold.',
    breakdown: '28 doubles + 56 trebles + 70 four-folds + 56 five-folds + 28 six-folds + 8 seven-folds + 1 eight-fold',
    bestFor: 'Maximum geometric returns on 8 selections',
    combinations: null,
  },
];

function SystemBets({ isMobile }) {
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [expandedSystem, setExpandedSystem] = useState(null);
  const [systemMode, setSystemMode] = useState('calculator'); // 'calculator' or 'ai'
  const [stake, setStake] = useState(1);
  const [selections, setSelections] = useState([]);
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [showCombos, setShowCombos] = useState(false);

  const system = SYSTEM_BETS.find(s => s.key === selectedSystem);

  // Initialize selections when system changes
  useEffect(() => {
    if (system) {
      setSelections(Array(system.selections).fill().map((_, i) => ({
        id: i,
        match: '',
        selection: '',
        odds: '',
      })));
      setAiResult(null);
    }
  }, [selectedSystem]);

  // Calculate returns for calculator mode
    function calcReturns() {
    if (!system || !system.combinations) return null;
    const validSelections = selections.filter(s => !isNaN(parseFloat(s.odds)) && parseFloat(s.odds) > 1);
    if (validSelections.length < system.selections) return null;

    const odds = validSelections.map(s => parseFloat(s.odds));
    const names = validSelections.map(s => s.selection || `Sel ${validSelections.indexOf(s) + 1}`);
    const combos = system.combinations(odds, names);
    const totalStake = system.bets * stake;
    const totalReturn = combos.reduce((sum, c) => sum + (c.payout * stake), 0);

    // Returns for different win scenarios
    const scenarios = [];
    for (let wins = system.minWins; wins <= system.selections; wins++) {
      // Calculate average return for this many winners
      const winningCombos = combos.filter(c => c.legs.length <= wins);
      const scenarioReturn = winningCombos.reduce((sum, c) => sum + (c.payout * stake), 0);
      scenarios.push({ wins, return: Number(scenarioReturn.toFixed(2)) });
    }

    return { combos, totalStake, totalReturn, scenarios };
  }

  async function buildAISystemBet() {
    setAiLoading(true);
    setAiError(null);
    setAiResult(null);
    try {
      const data = await apiFetch('/smart-parlay', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stake: stake * system.bets,
          riskLevel: 'balanced',
          customLegs: system.selections,
          targetDate: 'all',
          marketFilter: 'all',
        }),
      });
      setAiResult(data);
    } catch (err) {
      setAiError('Could not build system bet. Please try again.');
    } finally {
      setAiLoading(false);
    }
  }

  const results = selectedSystem && systemMode === 'calculator' ? calcReturns() : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* System type selector */}
      <div className="card" style={{ padding: '16px 20px' }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 12 }}>
          Choose System Type
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {SYSTEM_BETS.map(s => (
            <div key={s.key}>
              <div
                onClick={() => {
                  setSelectedSystem(s.key === selectedSystem ? null : s.key);
                  setExpandedSystem(expandedSystem === s.key ? null : s.key);
                }}
                style={{
                  padding: '12px 14px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  border: `1px solid ${selectedSystem === s.key ? 'var(--accent)' : 'var(--border)'}`,
                  background: selectedSystem === s.key ? 'rgba(0,229,176,0.06)' : 'var(--bg3)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  transition: 'all .12s',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                    {s.selections} selections · {s.bets} bets · Min {s.minWins} winner{s.minWins > 1 ? 's' : ''} to return
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                  {expandedSystem === s.key ? '▲' : '▼'}
                </div>
              </div>

              {/* Expandable details */}
              {expandedSystem === s.key && (
                <div style={{
                  padding: '14px 16px', background: 'var(--bg4)',
                  borderRadius: '0 0 var(--radius-sm) var(--radius-sm)',
                  border: '1px solid var(--border)', borderTop: 'none',
                }}>
                  <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 10 }}>
                    {s.description}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div style={{ background: 'var(--bg3)', borderRadius: 6, padding: '8px 10px' }}>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>Breakdown</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{s.breakdown}</div>
                    </div>
                    <div style={{ background: 'var(--bg3)', borderRadius: 6, padding: '8px 10px' }}>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>Best for</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{s.bestFor}</div>
                    </div>
                  </div>
                  <button
                    className="btn btn-primary"
                    style={{ width: '100%', marginTop: 10, fontSize: 12 }}
                    onClick={() => setSelectedSystem(s.key)}
                  >
                    Select {s.name}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* System bet builder */}
      {selectedSystem && system && (
        <>
          {/* Mode selector */}
          <div className="card" style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: 3, marginBottom: 16 }}>
              <button
                onClick={() => setSystemMode('calculator')}
                style={{
                  flex: 1, padding: '8px', fontSize: 12, fontWeight: 600,
                  borderRadius: 'var(--radius-sm)', border: 'none', cursor: 'pointer',
                  background: systemMode === 'calculator' ? 'var(--accent)' : 'transparent',
                  color: systemMode === 'calculator' ? '#000' : 'var(--text3)',
                }}
              >
                📊 Calculator
              </button>
              <button
                onClick={() => setSystemMode('ai')}
                style={{
                  flex: 1, padding: '8px', fontSize: 12, fontWeight: 600,
                  borderRadius: 'var(--radius-sm)', border: 'none', cursor: 'pointer',
                  background: systemMode === 'ai' ? 'var(--accent)' : 'transparent',
                  color: systemMode === 'ai' ? '#000' : 'var(--text3)',
                }}
              >
                🤖 AI Builder
              </button>
            </div>

            {/* Stake per bet */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', fontWeight: 600, marginBottom: 6 }}>
                Stake per bet (BWP)
                <span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400, marginLeft: 6 }}>
                  Total stake: P{(stake * system.bets).toFixed(2)}
                </span>
              </label>
              <input
                type="number"
                value={stake}
                onChange={e => setStake(parseFloat(e.target.value) || 1)}
                style={{ width: '100%', fontSize: 20, fontFamily: 'var(--font-display)', fontWeight: 700 }}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                {[1, 2, 5, 10, 20].map(a => (
                  <button key={a} className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => setStake(a)}>
                    P{a}
                  </button>
                ))}
              </div>
            </div>

            {/* Calculator mode — manual selections */}
            {systemMode === 'calculator' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 600 }}>
                  Enter your {system.selections} selections:
                </div>
                {selections.map((sel, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder={`Selection ${i + 1}`}
                      value={sel.selection}
                      onChange={e => {
                        const updated = [...selections];
                        updated[i] = { ...updated[i], selection: e.target.value };
                        setSelections(updated);
                      }}
                      style={{ fontSize: 13 }}
                    />
                    <input
                      type="number"
                      placeholder="Odds e.g. 1.85"
                      value={sel.odds}
                      onChange={e => {
                        const updated = [...selections];
                        updated[i] = { ...updated[i], odds: e.target.value };
                        setSelections(updated);
                      }}
                      style={{ fontSize: 13 }}
                    />
                    <div style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                      {sel.odds && !isNaN(parseFloat(sel.odds)) ? `${(1/parseFloat(sel.odds)*100).toFixed(0)}%` : '—'}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* AI Builder mode */}
            {systemMode === 'ai' && (
              <div>
                <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 12 }}>
                  Our AI will select the best {system.selections} value bets for your {system.name}. Each selection is chosen for genuine edge, not random picks.
                </div>
                <button
                  className="btn btn-primary"
                  onClick={buildAISystemBet}
                  disabled={aiLoading}
                  style={{ width: '100%', padding: '14px', fontSize: 15 }}
                >
                  {aiLoading ? '🤖 AI is building your system bet...' : `🎲 Build AI ${system.name}`}
                </button>
                {aiError && (
                  <div style={{ marginTop: 10, color: 'var(--danger)', fontSize: 12, textAlign: 'center' }}>
                    {aiError}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Calculator Results */}
          {systemMode === 'calculator' && results && (
            <div className="card" style={{ padding: '16px 20px' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 12 }}>
                📊 Returns Calculator
              </div>

              {/* Summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 16 }}>
                <div className="stat-card" style={{ '--accent-color': 'var(--accent)', textAlign: 'center' }}>
                  <div className="stat-label" style={{ fontSize: 10, textAlign: 'center' }}>Total Stake</div>
                  <div className="stat-value" style={{ fontSize: 18, textAlign: 'center' }}>P{results.totalStake.toFixed(2)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center' }}>{system.bets} bets @ P{stake}</div>
                </div>
                <div className="stat-card" style={{ '--accent-color': 'var(--success)', textAlign: 'center' }}>
                  <div className="stat-label" style={{ fontSize: 10, textAlign: 'center' }}>If All Win</div>
                  <div className="stat-value" style={{ fontSize: 18, textAlign: 'center', color: 'var(--success)' }}>P{results.totalReturn.toFixed(2)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center' }}>Maximum return</div>
                </div>
              </div>

              {/* Win scenarios */}
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>
                Returns by number of winners:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                {results.scenarios.map(s => (
                  <div key={s.wins} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 12px', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)',
                  }}>
                    <div style={{ fontSize: 12 }}>
                      {s.wins} winner{s.wins > 1 ? 's' : ''}
                      {s.wins === system.selections && ' (all)'}
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: s.return > results.totalStake ? 'var(--success)' : 'var(--danger)' }}>
                      P{s.return.toFixed(2)}
                      <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6 }}>
                        {s.return > results.totalStake ? `+P${(s.return - results.totalStake).toFixed(2)}` : `-P${(results.totalStake - s.return).toFixed(2)}`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Individual combinations toggle */}
              {results.combos && (
                <>
                  <button
                    className="btn btn-ghost"
                    style={{ fontSize: 11, marginBottom: 8 }}
                    onClick={() => setShowCombos(!showCombos)}
                  >
                    {showCombos ? '▲ Hide' : '▼ Show'} all {results.combos.length} combinations
                  </button>
                  {showCombos && (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>
                        All combinations:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {results.combos.map((c, i) => (
                          <div key={i} style={{
                            display: 'flex', justifyContent: 'space-between',
                            padding: '6px 10px', background: 'var(--bg3)', borderRadius: 6, fontSize: 11,
                          }}>
                            <span style={{ color: 'var(--text3)' }}>{c.name}</span>
                            <span style={{ fontWeight: 600 }}>P{(c.payout * stake).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
              <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(61,142,248,0.06)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>
                💡 <strong>How to place:</strong> Check if your bookmaker offers system bets natively (Lucky 15, Yankee etc). If yes, add your selections and choose the system type on the bet slip. If not, place each combination above as a separate bet manually.
              </div>
            </div>
          )}

          {/* AI Builder Results */}
          {systemMode === 'ai' && aiResult && !aiLoading && (
            <div className="card">
              <div className="section-header">
                <div className="section-title">🎲 AI {system.name} Selections</div>
              </div>
              {aiResult.parlay?.legs?.map((leg, i) => (
                <div key={i} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                        {i + 1}. ⚽ {leg.match}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{leg.league}</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <span className="pill pill-teal" style={{ fontSize: 10 }}>{leg.market} — {leg.selection}</span>
                        {leg.edge && <span className="pill pill-green" style={{ fontSize: 10 }}>+{leg.edge}% edge</span>}
                      </div>
                      {leg.reasoning && (
                        <div style={{ fontSize: 11, color: 'var(--text3)', fontStyle: 'italic', marginTop: 4 }}>
                          {leg.reasoning}
                        </div>
                      )}
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20, color: 'var(--accent)', flexShrink: 0, marginLeft: 12 }}>
                      {leg.odds}
                    </div>
                  </div>
                </div>
              ))}

              {/* AI system bet returns */}
              {aiResult.parlay?.legs?.length === system.selections && system.combinations && (() => {
                const odds = aiResult.parlay.legs.map(l => l.odds);
                const combos = system.combinations(odds);
                const totalStake = system.bets * stake;
                return (
                  <div style={{ padding: '16px' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>
                      Potential returns at P{stake} per bet (P{totalStake} total):
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {combos.map((c, i) => (
                        <div key={i} style={{
                          display: 'flex', justifyContent: 'space-between',
                          padding: '6px 10px', background: 'var(--bg3)', borderRadius: 6, fontSize: 11,
                        }}>
                          <span style={{ color: 'var(--text3)' }}>{c.name}</span>
                          <span style={{ fontWeight: 600 }}>P{(c.payout * stake).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div style={{ padding: '12px 16px', fontSize: 11, color: 'var(--text3)', textAlign: 'center' }}>
                ⚠️ Place each selection as part of your {system.name} at your preferred bookmaker. Total stake: P{(stake * system.bets).toFixed(2)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function SmartParlay() {
  const [preset, setPreset] = useState('balanced');
  const [stake, setStake] = useState(20);
  const [legs, setLegs] = useState(4);
  const [avgOdds, setAvgOdds] = useState(1.55);
  const [targetReturn, setTargetReturn] = useState(null);
  const [lastChanged, setLastChanged] = useState('legs'); // 'legs' or 'return'

  const [parlay, setParlay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [width, setWidth] = useState(window.innerWidth);

  const [targetDate, setTargetDate] = useState('all');
  const [marketFilter, setMarketFilter] = useState('all');

  const [mode, setMode] = useState('basic'); // 'basic' or 'pro'
  const [showProStats, setShowProStats] = useState(false);
  const [proParlay, setProParlay] = useState(null);
  const [proLoading, setProLoading] = useState(false);
  const [proError, setProError] = useState(null);
  const [dailyHistory, setDailyHistory] = useState([]);

    useEffect(() => {
    apiFetch('/daily-parlay/history?mode=pro&limit=30')
      .then(data => setDailyHistory(data.history || []))
      .catch(() => setDailyHistory([]));
  }, []);

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isMobile = width < 768;

  // Derived values
  const winProb = calcWinProb(legs, avgOdds);
  const estimatedReturn = calcReturn(stake, legs, avgOdds);
  const difficulty = getDifficultyIndicator(winProb);
  const ref100Return = calcReturn(100, legs, avgOdds);

  // When legs or avgOdds change, update targetReturn display
  useEffect(() => {
    if (lastChanged !== 'return') {
      setTargetReturn(estimatedReturn);
    }
  }, [legs, avgOdds, stake]);

  function applyPreset(p) {
    setPreset(p.key);
    setLegs(p.legs);
    setAvgOdds(p.avgOdds);
    setLastChanged('legs');
    setParlay(null);
    setError(null);
  }

  function handleLegsChange(newLegs) {
    setLegs(newLegs);
    setLastChanged('legs');
    setParlay(null);
  }

  function handleOddsChange(newOdds) {
    setAvgOdds(newOdds);
    if (lastChanged === 'return' && targetReturn) {
      const newLegs = calcLegsNeeded(stake, targetReturn, newOdds);
      setLegs(newLegs);
    }
    setParlay(null);
  }

  function handleReturnChange(newReturn) {
  setTargetReturn(newReturn);
  setLastChanged('return');
  // Calculate both legs and avgOdds needed
  const newLegs = Math.min(Math.max(Math.round(Math.log(newReturn / stake) / Math.log(avgOdds)), 2), 10);
  const newAvgOdds = Math.pow(newReturn / stake, 1 / newLegs);
  const clampedAvgOdds = Math.min(Math.max(Number(newAvgOdds.toFixed(2)), 1.20), 4.00);
  setLegs(newLegs);
  setAvgOdds(clampedAvgOdds);
  setParlay(null);
}

  function handleStakeChange(newStake) {
    setStake(newStake);
    setParlay(null);
  }

  async function buildParlay() {
    setLoading(true);
    setError(null);
    setParlay(null);
    try {
      const data = await apiFetch('/smart-parlay', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stake,
          riskLevel: preset,
          customLegs: legs,
          avgOdds,
          targetReturn,
          targetDate,
          marketFilter,
        }),
      });
      setParlay(data);
    } catch (err) {
      setError('Could not build parlay. Please try again.');
    } finally {
      setLoading(false);
    }
  }

async function buildProParlay() {
    setProLoading(true);
    setProError(null);
    setProParlay(null);
    try {
      const data = await apiFetch('/smart-parlay-pro', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stake,
          riskLevel: preset,
          customLegs: legs,
          avgOdds,
          targetReturn,
          targetDate,
          marketFilter,
        }),
      });
      setProParlay(data);
    } catch (err) {
      setProError('Could not build Pro parlay. Please try again.');
    } finally {
      setProLoading(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Watermark />

      {/* Header */}
      <div style={{
        background: 'rgba(0,229,176,0.04)', border: '1px solid rgba(0,229,176,0.15)',
        borderRadius: 'var(--radius)', padding: '16px 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14,
      }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flex: 1 }}>
          <div style={{ fontSize: 24, flexShrink: 0 }}>🃏</div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15 }}>AI Smart Parlay Builder</div>
              <span className="pill pill-blue" style={{ fontSize: 9 }}>BETA</span>
              {mode === 'pro' && <span className="pill pill-green" style={{ fontSize: 9 }}>⭐ PRO</span>}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6 }}>
              {mode === 'basic'
                ? 'Our AI selects the best value bets for your parlay. Choose a preset or customise your own — every leg is selected for genuine edge, not random picks.'
                : 'Pro mode uses Poisson distribution, team form analysis and sharp money confirmation to build smarter parlays with higher confidence selections.'}
            </div>
          </div>
        </div>

        {/* Mode toggle */}
                <div style={{ display: 'flex', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: 3, flexShrink: 0 }}>
          <button
            onClick={() => { setMode('basic'); setParlay(null); setProParlay(null); }}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 600,
              borderRadius: 'var(--radius-sm)', border: 'none', cursor: 'pointer',
              background: mode === 'basic' ? 'var(--accent)' : 'transparent',
              color: mode === 'basic' ? '#000' : 'var(--text3)',
              transition: 'all .15s',
            }}
          >
            Basic
          </button>
          <button
            onClick={() => { setMode('pro'); setParlay(null); setProParlay(null); }}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 600,
              borderRadius: 'var(--radius-sm)', border: 'none', cursor: 'pointer',
              background: mode === 'pro' ? 'var(--accent)' : 'transparent',
              color: mode === 'pro' ? '#000' : 'var(--text3)',
              transition: 'all .15s',
            }}
          >
            ⭐ Pro
          </button>
          <button
            onClick={() => { setMode('system'); setParlay(null); setProParlay(null); }}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 600,
              borderRadius: 'var(--radius-sm)', border: 'none', cursor: 'pointer',
              background: mode === 'system' ? 'var(--accent)' : 'transparent',
              color: mode === 'system' ? '#000' : 'var(--text3)',
              transition: 'all .15s',
            }}
          >
            🎲 System
          </button>
        </div>
      </div>

      {/* Pro Stats — only show in Pro mode */}
      {mode === 'pro' && (
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showProStats ? 16 : 0 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13 }}>
              📊 Pro Performance Stats
            </div>
            <button
              className="btn btn-ghost"
              style={{ fontSize: 11 }}
              onClick={() => setShowProStats(!showProStats)}
            >
              {showProStats ? 'Hide Stats ▲' : 'Show Stats ▼'}
            </button>
          </div>

          {showProStats && (
            <div>
              {dailyHistory.length === 0 ? (
                <div style={{
                  textAlign: 'center', padding: '32px 20px',
                  background: 'var(--bg3)', borderRadius: 'var(--radius-sm)',
                }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>📈</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
                    Performance History Coming Soon
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6, maxWidth: 400, margin: '0 auto' }}>
                    Once the Daily Parlay feature launches, this section will show:
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 10, marginTop: 16, textAlign: 'left' }}>
                    {[
                      { icon: '🎯', title: 'Win Rate', desc: 'Historical win percentage of AI-selected parlays' },
                      { icon: '💰', title: 'P20/Day Growth', desc: 'How your account would grow betting P20 daily' },
                      { icon: '📅', title: 'Last 10 Results', desc: 'Win/Loss timeline of recent daily parlays' },
                      { icon: '🏆', title: 'Best Win', desc: 'Biggest winning parlay and return achieved' },
                    ].map(item => (
                      <div key={item.title} style={{
                        padding: '12px 14px', background: 'var(--bg4)',
                        borderRadius: 'var(--radius-sm)',
                      }}>
                        <div style={{ fontSize: 18, marginBottom: 4 }}>{item.icon}</div>
                        <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 2 }}>{item.title}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>{item.desc}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 16, fontSize: 12, color: 'var(--text3)' }}>
                    🚀 Daily Parlay launches soon — stats will populate automatically
                  </div>
                </div>
                            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  
                  {/* Stats Summary */}
                  {(() => {
                    const settled = dailyHistory.filter(p => p.status !== 'pending');
                    const wins = settled.filter(p => p.status === 'won');
                    const losses = settled.filter(p => p.status === 'lost');
                    const winRate = settled.length ? ((wins.length / settled.length) * 100).toFixed(1) : 0;
                    const totalStaked = settled.length * 20;
                    const totalReturned = wins.reduce((sum, p) => sum + (p.actualReturn || p.potentialReturn || 0), 0);
                    const profit = totalReturned - totalStaked;
                    const roi = totalStaked ? ((profit / totalStaked) * 100).toFixed(1) : 0;
                    const bestWin = wins.reduce((best, p) => {
                      const ret = p.actualReturn || p.potentialReturn || 0;
                      return ret > (best?.ret || 0) ? { ret, date: p.date } : best;
                    }, null);

                    return (
                      <>
                        {/* Key Stats */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                          {[
                            { label: 'Win Rate', value: `${winRate}%`, sub: `${wins.length}W / ${losses.length}L`, color: 'var(--accent)' },
                            { label: 'Total ROI', value: `${roi > 0 ? '+' : ''}${roi}%`, sub: `P${profit.toFixed(0)} profit`, color: profit >= 0 ? 'var(--success)' : 'var(--danger)' },
                            { label: 'Total Staked', value: `P${totalStaked}`, sub: `${settled.length} parlays`, color: 'var(--text2)' },
                            { label: 'Best Win', value: bestWin ? `P${bestWin.ret.toFixed(0)}` : '—', sub: bestWin?.date || '—', color: '#f59e0b' },
                          ].map(s => (
                            <div key={s.label} style={{ background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', textAlign: 'center' }}>
                              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{s.label}</div>
                              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: s.color }}>{s.value}</div>
                              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{s.sub}</div>
                            </div>
                          ))}
                        </div>

                        {/* Last 10 Results Timeline */}
                        <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: '12px 14px' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Last 10 Results</div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {dailyHistory.slice(0, 10).map((p, i) => (
                              <div key={i} style={{
                                width: 36, height: 36, borderRadius: 6,
                                background: p.status === 'won' ? 'rgba(0,229,176,0.15)' : p.status === 'lost' ? 'rgba(240,71,71,0.15)' : 'var(--bg4)',
                                border: `1px solid ${p.status === 'won' ? 'var(--accent)' : p.status === 'lost' ? 'var(--danger)' : 'var(--border)'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 14, cursor: 'default',
                                title: p.date,
                              }}>
                                {p.status === 'won' ? '✅' : p.status === 'lost' ? '❌' : '⏳'}
                              </div>
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 11, color: 'var(--text3)' }}>
                            <span>✅ Won</span>
                            <span>❌ Lost</span>
                            <span>⏳ Pending</span>
                          </div>
                        </div>

                        {/* P20/Day Growth Simulation */}
                        <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: '12px 14px' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>P20/Day Bankroll Simulation</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>Starting from P0, betting P20 on each daily parlay</div>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 60 }}>
                            {(() => {
                              let bankroll = 0;
                              const points = [0];
                              [...dailyHistory].reverse().forEach(p => {
                                if (p.status === 'won') bankroll += (p.actualReturn || p.potentialReturn || 0) - 20;
                                else if (p.status === 'lost') bankroll -= 20;
                                points.push(bankroll);
                              });
                              const max = Math.max(...points.map(Math.abs), 1);
                              return points.slice(-20).map((val, i) => (
                                <div key={i} style={{
                                  flex: 1, minWidth: 4,
                                  height: `${Math.max((Math.abs(val) / max) * 100, 4)}%`,
                                  background: val >= 0 ? 'var(--accent)' : 'var(--danger)',
                                  borderRadius: 2, opacity: 0.8,
                                }} />
                              ));
                            })()}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: 'var(--text3)' }}>
                            <span>Oldest</span>
                            <span>Latest</span>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Presets */}
      <div className="card" style={{ padding: '16px 20px', display: mode === 'system' ? 'none' : 'block' }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 12 }}>
          Quick Presets
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 8 }}>
          {PRESETS.map(p => (
            <div
              key={p.key}
              onClick={() => applyPreset(p)}
              style={{
                padding: '10px 12px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                border: `1px solid ${preset === p.key ? p.color : 'var(--border)'}`,
                background: preset === p.key ? `rgba(0,229,176,0.06)` : 'var(--bg3)',
                transition: 'all .12s',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 3 }}>{p.label}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>{p.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="card" style={{ padding: '16px 20px', display: mode === 'system' ? 'none' : 'block' }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 16 }}>
          Customise Your Parlay
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>

          {/* Stake */}
          <div>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', fontWeight: 600, marginBottom: 6 }}>
              Your Stake (BWP)
            </label>
            <input
              type="number"
              value={stake}
              onChange={e => handleStakeChange(parseFloat(e.target.value) || 20)}
              style={{ width: '100%', fontSize: 20, fontFamily: 'var(--font-display)', fontWeight: 700 }}
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {[20, 50, 100, 200, 500].map(a => (
                <button key={a} className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => handleStakeChange(a)}>
                  P{a}
                </button>
              ))}
            </div>
          </div>

          {/* Target Return */}
          <div>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', fontWeight: 600, marginBottom: 6 }}>
              Desired Return (BWP)
              <span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400, marginLeft: 6 }}>— adjusts legs automatically</span>
            </label>
            <input
  type="number"
  value={lastChanged === 'return' ? (targetReturn === '' ? '' : (targetReturn ?? estimatedReturn)) : estimatedReturn}
  onChange={e => {
    const raw = e.target.value;
    if (raw === '' || raw === '0') {
      setTargetReturn('');
      return;
    }
    const val = parseFloat(raw);
    if (!isNaN(val)) {
      handleReturnChange(val);
    }
  }}
  onFocus={() => setLastChanged('return')}
  placeholder={`P${estimatedReturn.toFixed(2)}`}
  style={{ width: '100%', fontSize: 20, fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--success)' }}
/>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {[500, 1000, 5000, 10000, 50000].map(a => (
                <button key={a} className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => handleReturnChange(a)}>
                  {formatReturn(a)}
                </button>
              ))}
            </div>
          </div>

          {/* Avg Odds Slider */}
          <div>
            <label style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text2)', fontWeight: 600, marginBottom: 6 }}>
              <span>Average Odds per Leg</span>
              <span style={{ fontFamily: 'var(--font-display)', color: 'var(--accent)' }}>{avgOdds.toFixed(2)}</span>
            </label>
            <input
              type="range"
              min="1.20" max="4.00" step="0.05"
              value={avgOdds}
              onChange={e => handleOddsChange(parseFloat(e.target.value))}
              style={{ width: '100%' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
              <span>1.20 — Short favourites</span>
              <span>4.00 — Big outsiders</span>
            </div>
          </div>

          {/* Legs Slider */}
          <div>
            <label style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text2)', fontWeight: 600, marginBottom: 6 }}>
              <span>Number of Legs</span>
              <span style={{ fontFamily: 'var(--font-display)', color: 'var(--accent)' }}>{legs}</span>
            </label>
              <input
              type="range"
              min="2" max={mode === 'pro' ? 20 : 10} step="1"
              value={legs}
              onChange={e => handleLegsChange(parseInt(e.target.value))}
              style={{ width: '100%' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
              <span>2 — Safer</span>
              <span>{mode === 'pro' ? '20' : '10'} — Bigger return</span>
            </div>
          </div>
        </div>
      </div>

      {/* Date & Market Filters */}
      <div className="card" style={{ padding: '16px 20px', display: mode === 'system' ? 'none' : 'block' }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 16 }}>
          Filters
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Date filter */}
          <div>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', fontWeight: 600, marginBottom: 8 }}>
              Match Date
            </label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[
                { key: 'all', label: '📅 All' },
                { key: 'today', label: '🌅 Today' },
                { key: 'tomorrow', label: '🌄 Tomorrow' },
                { key: 'weekend', label: '🏆 Weekend' },
              ].map(d => (
                <button
                  key={d.key}
                  onClick={() => { setTargetDate(d.key); setParlay(null); }}
                  className={targetDate === d.key ? 'btn btn-primary' : 'btn btn-outline'}
                  style={{ fontSize: 12, padding: '6px 14px' }}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Market filter */}
          <div>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', fontWeight: 600, marginBottom: 8 }}>
              Market Type
            </label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[
                { key: 'all', label: '🎯 All Markets' },
                { key: '1X2', label: '1X2' },
                { key: 'Over/Under 1.5', label: 'O/U 1.5' },
                { key: 'Over/Under 2.5', label: 'O/U 2.5' },
                { key: 'Over/Under 3.5', label: 'O/U 3.5' },
                { key: 'BTTS', label: 'BTTS' },
                { key: 'Double Chance', label: 'Double Chance' },
              ].map(m => (
                <button
                  key={m.key}
                  onClick={() => { setMarketFilter(m.key); setParlay(null); }}
                  className={marketFilter === m.key ? 'btn btn-primary' : 'btn btn-outline'}
                  style={{ fontSize: 12, padding: '6px 14px' }}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Live Estimates */}
      <div className="card" style={{ padding: '16px 20px', display: mode === 'system' ? 'none' : 'block' }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 12 }}>
          Live Estimates
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 14 }}>
          <div className="stat-card" style={{ '--accent-color': 'var(--success)', textAlign: 'center' }}>
            <div className="stat-label" style={{ fontSize: 10, textAlign: 'center' }}>Potential Return</div>
            <div className="stat-value" style={{ fontSize: isMobile ? 16 : 20, textAlign: 'center', color: 'var(--success)' }}>{formatReturn(estimatedReturn)}</div>
            <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', marginTop: 2 }}>from P{stake} stake</div>
          </div>
          <div className="stat-card" style={{ '--accent-color': difficulty.color, textAlign: 'center' }}>
            <div className="stat-label" style={{ fontSize: 10, textAlign: 'center' }}>Win Probability</div>
            <div className="stat-value" style={{ fontSize: isMobile ? 16 : 20, textAlign: 'center', color: difficulty.color }}>{winProb}%</div>
            <div style={{ fontSize: 10, color: difficulty.color, textAlign: 'center', marginTop: 2 }}>{difficulty.icon} {difficulty.label}</div>
          </div>
          <div className="stat-card" style={{ '--accent-color': 'var(--accent)', textAlign: 'center' }}>
            <div className="stat-label" style={{ fontSize: 10, textAlign: 'center' }}>Combined Odds</div>
            <div className="stat-value" style={{ fontSize: isMobile ? 16 : 20, textAlign: 'center' }}>{Math.pow(avgOdds, legs).toFixed(1)}x</div>
            <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', marginTop: 2 }}>{legs} legs @ {avgOdds.toFixed(2)} avg</div>
          </div>
          <div className="stat-card" style={{ '--accent-color': 'var(--accent2)', textAlign: 'center' }}>
            <div className="stat-label" style={{ fontSize: 10, textAlign: 'center' }}>At P100 Stake</div>
            <div className="stat-value" style={{ fontSize: isMobile ? 16 : 20, textAlign: 'center' }}>{formatReturn(ref100Return)}</div>
            <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', marginTop: 2 }}>reference return</div>
          </div>
        </div>

        {/* Explanation */}
        <div style={{ padding: '10px 14px', background: 'rgba(61,142,248,0.06)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>
          💡 <strong>How it works:</strong> More legs = bigger potential return but lower chance of winning. Higher odds = bigger return but each leg is harder to win. The AI selects legs with genuine value edge — not random picks.
        </div>

        <button
          className="btn btn-primary"
          onClick={mode === 'pro' ? buildProParlay : buildParlay}
          disabled={loading || proLoading}
          style={{ width: '100%', padding: '14px', fontSize: 15, marginTop: 16 }}
        >
          {(loading || proLoading)
            ? '🤖 AI is building your parlay...'
            : mode === 'pro'
              ? `⭐ Build ${legs}-Leg Pro Parlay`
              : `🃏 Build ${legs}-Leg Smart Parlay`}
        </button>
      </div>

      {/* System Bets */}
      {mode === 'system' && (
        <SystemBets isMobile={isMobile} />
      )}

      {/* Loading */}
      {loading && (
        <div className="card" style={{ padding: 24 }}>
          <div className="skeleton" style={{ height: 20, marginBottom: 10 }} />
          <div className="skeleton" style={{ height: 20, marginBottom: 10 }} />
          <div className="skeleton" style={{ height: 20 }} />
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 12, textAlign: 'center' }}>
            AI is analysing value bets and building your {legs}-leg parlay...
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="card" style={{ padding: 20, textAlign: 'center', color: 'var(--danger)' }}>
          {error}
        </div>
      )}

      {/* Result */}
      {parlay && !loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
            <div className="stat-card" style={{ '--accent-color': 'var(--accent)', textAlign: 'center' }}>
              <div className="stat-label" style={{ fontSize: 10, textAlign: 'center' }}>Combined Odds</div>
              <div className="stat-value" style={{ fontSize: isMobile ? 16 : 20, textAlign: 'center' }}>{parlay.parlay?.combinedOdds}x</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', marginTop: 2 }}>{parlay.parlay?.legs?.length} legs selected</div>
            </div>
            <div className="stat-card" style={{ '--accent-color': 'var(--success)', textAlign: 'center' }}>
              <div className="stat-label" style={{ fontSize: 10, textAlign: 'center' }}>If Wins</div>
              <div className="stat-value" style={{ fontSize: isMobile ? 16 : 20, textAlign: 'center' }}>{formatReturn(parlay.parlay?.potentialReturn)}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', marginTop: 2 }}>Total return</div>
            </div>
            <div className="stat-card" style={{ '--accent-color': 'var(--accent2)', textAlign: 'center', gridColumn: '1 / -1' }}>
              <div className="stat-label" style={{ fontSize: 10, textAlign: 'center' }}>Win Probability</div>
              <div className="stat-value" style={{ fontSize: isMobile ? 16 : 20, textAlign: 'center' }}>{parlay.parlay?.combinedWinProbability}%</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', marginTop: 2 }}>Estimated chance of winning</div>
            </div>
          </div>

          {/* Legs */}
          <div className="card">
            <div className="section-header">
              <div className="section-title">🎯 Parlay Legs</div>
            </div>
            {parlay.parlay?.legs?.map((leg, j) => (
              <div key={j} style={{ padding: isMobile ? '12px 14px' : '14px 18px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                      ⚽ {leg.match}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>{leg.league}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span className="pill pill-teal" style={{ fontSize: 10 }}>{leg.market} — {leg.selection}</span>
                      <span className="pill pill-blue" style={{ fontSize: 10 }}>{BOOKMAKER_LABELS[leg.bookmaker] || leg.bookmaker}</span>
                      {leg.edge && <span className="pill pill-green" style={{ fontSize: 10 }}>+{leg.edge}% edge</span>}
                      {leg.winProbability && <span className="pill pill-amber" style={{ fontSize: 10 }}>{leg.winProbability}% prob</span>}
                    </div>
                    {leg.reasoning && (
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontStyle: 'italic', lineHeight: 1.5 }}>{leg.reasoning}</div>
                    )}
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: isMobile ? 18 : 22, color: 'var(--accent)', flexShrink: 0 }}>
                    {leg.odds}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* AI Reasoning */}
          {parlay.parlay?.parlayReasoning && (
            <div className="card" style={{ padding: '16px 20px' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 10 }}>
                🤖 AI Reasoning
              </div>
              <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>
                {parlay.parlay.parlayReasoning}
              </div>
              {parlay.parlay?.riskWarning && (
                <div style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(240,71,71,0.06)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--text3)' }}>
                  ⚠️ {parlay.parlay.riskWarning}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px' }}>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>
              Generated {parlay.generatedAt ? new Date(parlay.generatedAt).toLocaleTimeString() : ''}
              {parlay.fromCache ? ' · cached' : ''}
            </span>
            <button className="btn btn-outline" style={{ fontSize: 11 }} onClick={buildParlay}>
              ↺ Rebuild
            </button>
          </div>

          <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', padding: '0 20px' }}>
            ⚠️ AI-generated parlays are for informational purposes only. Always verify odds with bookmakers before betting. Bet responsibly.
          </div>
        </div>
      )}

      {/* Pro Result */}
      {proParlay && !proLoading && mode === 'pro' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Pro Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
            <div className="stat-card" style={{ '--accent-color': 'var(--accent)', textAlign: 'center' }}>
              <div className="stat-label" style={{ fontSize: 10, textAlign: 'center' }}>Combined Odds</div>
              <div className="stat-value" style={{ fontSize: isMobile ? 16 : 20, textAlign: 'center' }}>{proParlay.parlay?.combinedOdds}x</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', marginTop: 2 }}>{proParlay.parlay?.legs?.length} legs selected</div>
            </div>
            <div className="stat-card" style={{ '--accent-color': 'var(--success)', textAlign: 'center' }}>
              <div className="stat-label" style={{ fontSize: 10, textAlign: 'center' }}>If Wins</div>
              <div className="stat-value" style={{ fontSize: isMobile ? 16 : 20, textAlign: 'center' }}>{formatReturn(proParlay.parlay?.potentialReturn)}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', marginTop: 2 }}>Total return</div>
            </div>
            <div className="stat-card" style={{ '--accent-color': 'var(--accent2)', textAlign: 'center' }}>
              <div className="stat-label" style={{ fontSize: 10, textAlign: 'center' }}>Win Probability</div>
              <div className="stat-value" style={{ fontSize: isMobile ? 16 : 20, textAlign: 'center' }}>{proParlay.parlay?.combinedWinProbability}%</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', marginTop: 2 }}>Poisson-adjusted estimate</div>
            </div>
            <div className="stat-card" style={{ 
              '--accent-color': proParlay.parlay?.avgConfidence === 'HIGH' ? 'var(--success)' : proParlay.parlay?.avgConfidence === 'MEDIUM' ? 'var(--warning)' : 'var(--danger)',
              textAlign: 'center' 
            }}>
              <div className="stat-label" style={{ fontSize: 10, textAlign: 'center' }}>Avg Confidence</div>
              <div className="stat-value" style={{ fontSize: isMobile ? 16 : 20, textAlign: 'center' }}>
                {proParlay.parlay?.avgConfidence === 'HIGH' ? '🟢' : proParlay.parlay?.avgConfidence === 'MEDIUM' ? '🟡' : '🔴'} {proParlay.parlay?.avgConfidence}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', marginTop: 2 }}>Based on all signals</div>
            </div>
          </div>

          {/* Pro Legs */}
          <div className="card">
            <div className="section-header">
              <div className="section-title">⭐ Pro Parlay Legs</div>
            </div>
            {proParlay.parlay?.legs?.map((leg, j) => (
              <div key={j} style={{ padding: isMobile ? '12px 14px' : '16px 20px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Match name and confidence */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        ⚽ {leg.match}
                      </div>
                      <span style={{ 
                        fontSize: 9, padding: '2px 6px', borderRadius: 4, flexShrink: 0,
                        background: leg.confidence === 'HIGH' ? 'rgba(18,201,142,0.15)' : leg.confidence === 'MEDIUM' ? 'rgba(245,166,35,0.15)' : 'rgba(240,71,71,0.15)',
                        color: leg.confidence === 'HIGH' ? 'var(--success)' : leg.confidence === 'MEDIUM' ? 'var(--warning)' : 'var(--danger)',
                      }}>
                        {leg.confidence === 'HIGH' ? '🟢' : leg.confidence === 'MEDIUM' ? '🟡' : '🔴'} {leg.confidence}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>{leg.league}</div>

                    {/* Pills */}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                      <span className="pill pill-teal" style={{ fontSize: 10 }}>{leg.market} — {leg.selection}</span>
                      <span className="pill pill-blue" style={{ fontSize: 10 }}>{BOOKMAKER_LABELS[leg.bookmaker] || leg.bookmaker}</span>
                      {leg.edge && <span className="pill pill-green" style={{ fontSize: 10 }}>+{leg.edge}% edge</span>}
                    </div>

                    {/* Pro data grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginBottom: 8 }}>
                      <div style={{ background: 'var(--bg3)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                        <div style={{ fontSize: 9, color: 'var(--text3)', marginBottom: 2 }}>Poisson Prob</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: leg.poissonProb ? 'var(--accent)' : 'var(--text3)' }}>
                          {leg.poissonProb ? `${leg.poissonProb}%` : 'N/A'}
                        </div>
                      </div>
                      <div style={{ background: 'var(--bg3)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                        <div style={{ fontSize: 9, color: 'var(--text3)', marginBottom: 2 }}>Sharp Money</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: leg.sharpConfirmed ? 'var(--success)' : 'var(--text3)' }}>
                          {leg.sharpConfirmed ? '✅ Yes' : '❌ No'}
                        </div>
                      </div>
                      <div style={{ background: 'var(--bg3)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                        <div style={{ fontSize: 9, color: 'var(--text3)', marginBottom: 2 }}>Form Score</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: leg.formScore >= 0.6 ? 'var(--success)' : leg.formScore >= 0.4 ? 'var(--warning)' : 'var(--danger)' }}>
                          {leg.formScore ? `${(leg.formScore * 100).toFixed(0)}%` : 'N/A'}
                        </div>
                      </div>
                    </div>

                    {/* Form details */}
                    {leg.homeForm && (
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>
                        🏠 {leg.homeForm.teamName}: {leg.homeForm.form} | Avg scored: {leg.homeForm.avgScored}
                      </div>
                    )}
                    {leg.awayForm && (
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>
                        ✈️ {leg.awayForm.teamName}: {leg.awayForm.form} | Avg scored: {leg.awayForm.avgScored}
                      </div>
                    )}

                    {/* Sharp reason */}
                    {leg.sharpReason && leg.sharpReason !== 'No sharp money data' && leg.sharpReason !== 'No relevant sharp moves' && (
                      <div style={{ fontSize: 10, color: 'var(--success)', marginBottom: 4 }}>
                        📊 {leg.sharpReason}
                      </div>
                    )}

                    {/* AI Reasoning */}
                    {leg.reasoning && (
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontStyle: 'italic', lineHeight: 1.5, marginTop: 4 }}>
                        💡 {leg.reasoning}
                      </div>
                    )}
                  </div>

                  {/* Odds */}
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: isMobile ? 18 : 22, color: 'var(--accent)', flexShrink: 0 }}>
                    {leg.odds}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* AI Reasoning */}
          {proParlay.parlay?.parlayReasoning && (
            <div className="card" style={{ padding: '16px 20px' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 10 }}>
                🤖 Pro AI Analysis
              </div>
              <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>
                {proParlay.parlay.parlayReasoning}
              </div>
              {proParlay.parlay?.riskWarning && (
                <div style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(240,71,71,0.06)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--text3)' }}>
                  ⚠️ {proParlay.parlay.riskWarning}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px' }}>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>
              Generated {proParlay.generatedAt ? new Date(proParlay.generatedAt).toLocaleTimeString() : ''}
              {proParlay.fromCache ? ' · cached' : ''}
            </span>
            <button className="btn btn-outline" style={{ fontSize: 11 }} onClick={buildProParlay}>
              ↺ Rebuild
            </button>
          </div>

          <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', padding: '0 20px' }}>
            ⚠️ Pro parlays use Poisson distribution and form analysis. These are AI-generated selections — always verify odds with bookmakers before betting. Bet responsibly.
          </div>
        </div>
      )}
    </div>
  );
}